import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// recharts kept for future use
import { Building2, RefreshCw, AlertCircle, Users, Briefcase, Gift, ClipboardList } from 'lucide-react';
import api from '../services/api';

// ── Brand colours — navy + gray only, green/red kept for pass/fail status only
const C = {
  navy:    '#1e2d5e',
  coral:   '#f07060',
  emerald: '#10b981',  // kept for pass/accepted status only
  red:     '#ef4444',  // kept for rejected/fail status only
  slate:   '#f8fafc',
  border:  '#e2e8f0',
  // legacy aliases — mapped to navy to avoid touching every callsite
  indigo:  '#1e2d5e',
  orange:  '#1e2d5e',
  violet:  '#1e2d5e',
};

const fmt   = n => (n ?? 0);
const pct   = (a, b) => (!b ? 0 : Math.round((a / b) * 100));
const avg   = arr => arr.length ? Math.round(arr.reduce((s, v) => s + v, 0) / arr.length) : null;

// ── Metric block — uniform size, coral accent on value ───────────────────────
function MetricBlock({ label, value, unit = '', sub }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500 font-medium">{label}</span>
      <div className="flex items-baseline gap-0.5">
        <span className="text-2xl font-black text-[#f07060]">{value}</span>
        {unit && <span className="text-sm font-bold text-[#f07060]">{unit}</span>}
        {sub && <span className="text-xs text-gray-400 ml-1">{sub}</span>}
      </div>
    </div>
  );
}

// ── Card wrapper ──────────────────────────────────────────────────────────────
function Card({ title, children, className = '' }) {
  return (
    <div className={`rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden ${className}`}>
      {title && (
        <div className="px-5 py-3.5 border-b border-gray-100">
          <h3 className="font-bold text-gray-800 text-sm tracking-wide uppercase">{title}</h3>
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}


// ── Main ──────────────────────────────────────────────────────────────────────
// ── Date range presets ────────────────────────────────────────────────────────
const DATE_PRESETS = [
  { label: 'Today',         days: 0   },
  { label: 'Last 7 Days',   days: 7   },
  { label: 'Last 30 Days',  days: 30  },
  { label: 'Last 3 Months', days: 90  },
  { label: 'Last 6 Months', days: 180 },
  { label: 'Last 12 Months',days: 365 },
  { label: 'All Time',      days: null },
  { label: 'Custom',        days: -1  },
];

function getDateRange(preset) {
  if (!preset || preset.days === null) return { startDate: null, endDate: null };
  if (preset.days === -1) return null; // custom — caller handles
  const end   = new Date();
  const start = new Date();
  if (preset.days === 0) {
    start.setHours(0, 0, 0, 0);
  } else {
    start.setDate(start.getDate() - preset.days);
  }
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}

export default function UserActivityDashboard({ userId, userName }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [company, setCompany]     = useState('all');
  const [companyId, setCompanyId] = useState('all');
  const [activeStage, setActiveStage] = useState(null);
  const [preset, setPreset]       = useState(DATE_PRESETS[2]);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');
  // Separate companies list — fetched once, never lost on filter change
  const [allCompanies, setAllCompanies] = useState([]); // [{ _id, name }]
  const navigate = useNavigate();

  // Fetch all companies once on mount
  useEffect(() => {
    api.get('/companies').then(r => {
      const list = r.data?.data || r.data?.companies || r.data || [];
      if (Array.isArray(list)) {
        setAllCompanies(list.map(c => ({ _id: c._id, name: c.name || c.companyName })).filter(c => c.name));
      }
    }).catch(() => {});
  }, []);

  // Core load — always takes explicit values to avoid stale closure issues
  const loadWith = async ({ pArg, cidArg, startArg, endArg } = {}) => {
    if (!userId) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startArg) params.append('startDate', startArg);
      if (endArg)   params.append('endDate',   endArg);
      if (cidArg && cidArg !== 'all') params.append('companyId', cidArg);
      const r = await api.get(`/admin/users/${userId}/activity?${params.toString()}`);
      setData(r.data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // Helper: compute date range from preset + custom dates
  const currentDates = (p = preset) => {
    if (p.days === -1) return { startArg: customStart || null, endArg: customEnd || null };
    const range = getDateRange(p);
    return { startArg: range?.startDate || null, endArg: range?.endDate || null };
  };

  // Public reload — uses current state
  const load = () => loadWith({ cidArg: companyId, ...currentDates() });

  useEffect(() => { loadWith({ ...currentDates(DATE_PRESETS[2]) }); }, [userId]); // eslint-disable-line

  const handleCompanyChange = (name) => {
    const found = allCompanies.find(c => c.name === name);
    const id = name === 'all' ? 'all' : (found?._id || 'all');
    setCompany(name);
    setCompanyId(id);
    loadWith({ cidArg: id, ...currentDates() });
  };

  const handlePresetChange = (p) => {
    setPreset(p);
    if (p.days !== -1) {
      const { startArg, endArg } = currentDates(p);
      loadWith({ cidArg: companyId, startArg, endArg });
    }
  };

  // companies shown in dropdown — always the full list, never filtered
  const companies = allCompanies;

  // fc still used for client-side filtering of stageDetails (company filter on name)
  const fc = arr => company === 'all' ? (arr || []) : (arr || []).filter(i => i.companyName === company);

  // stageDetail must be declared before early returns (hooks rule)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stageDetail = useMemo(() => {
    if (!data || !activeStage || activeStage === 'assessment') return null;
    const sd2     = data.stageDetails || {};
    const jSummary = data.jobSummary  || {};
    const rr      = fc(sd2.resumeScreening || []);
    const oo      = fc(sd2.offers         || []);
    const tJDs    = jSummary.total ?? data.statistics?.totalJobs ?? 0;
    const sl      = rr.filter(r => r.status === 'shortlisted').length;
    const rej     = rr.filter(r => r.status === 'rejected').length;
    const sent    = oo.filter(o => ['sent','accepted'].includes(o.status)).length;
    const acc     = oo.filter(o => o.status === 'accepted').length;
    const map = {
      jd:      { title: 'Job Descriptions', items: [], pills: [
        { label: 'Total',       value: tJDs,                        cls: 'bg-indigo-50 text-indigo-700'   },
        { label: 'In-Progress', value: jSummary.inProgress ?? 0,   cls: 'bg-[#f07060]/10 text-[#f07060]' },
        { label: 'Completed',   value: jSummary.completed  ?? 0,   cls: 'bg-emerald-50 text-emerald-700' },
      ]},
      media:   { title: 'Media & Content', items: [], pills: [
        { label: 'With Content', value: jSummary.withMediaPlan ?? 0,          cls: 'bg-indigo-50 text-indigo-700' },
        { label: 'Total JDs',    value: tJDs,                                  cls: 'bg-gray-100 text-gray-600'    },
        { label: 'Without',      value: tJDs - (jSummary.withMediaPlan ?? 0), cls: 'bg-gray-100 text-gray-500'    },
      ]},
      resumes: { title: 'Resumes', items: rr, pills: [
        { label: 'Total',       value: rr.length, cls: 'bg-indigo-50 text-indigo-700'   },
        { label: 'Shortlisted', value: sl,        cls: 'bg-emerald-50 text-emerald-700' },
        { label: 'Rejected',    value: rej,       cls: 'bg-red-50 text-red-600'         },
      ]},
      offers:  { title: 'Offer Letters', items: oo, pills: [
        { label: 'Generated', value: oo.filter(o=>o.status==='generated').length, cls: 'bg-indigo-50 text-indigo-700'   },
        { label: 'Sent',      value: sent,                                         cls: 'bg-[#f07060]/10 text-[#f07060]' },
        { label: 'Accepted',  value: acc,                                          cls: 'bg-emerald-50 text-emerald-700' },
      ]},
    };
    return map[activeStage] || null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStage, data, company]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e2d5e]" />
    </div>
  );
  if (error) return <div className="p-6 text-red-500 flex items-center gap-2"><AlertCircle className="w-5 h-5" />{error}</div>;
  if (!data) return null;

  const { statistics, stageDetails: sd = {}, jobSummary = {}, pipelineStages = [] } = data;

  const resumes   = fc(sd.resumeScreening   || []);
  const preS      = fc(sd.preScreening      || []);
  const psycho    = fc(sd.psychoanalytical  || []);
  const finals    = fc(sd.finalInterview    || []);
  const offers    = fc(sd.offers            || []);

  const totalJDs      = jobSummary.total      ?? statistics?.totalJobs ?? 0;
  const inProgressJDs = jobSummary.inProgress ?? statistics?.activeJobs ?? 0;
  const shortlisted   = resumes.filter(r => r.status === 'shortlisted').length;
  const rejected      = resumes.filter(r => r.status === 'rejected').length;
  const offersSent    = offers.filter(o => ['sent','accepted'].includes(o.status)).length;
  const accepted      = offers.filter(o => o.status === 'accepted').length;
  const offerAccRate  = pct(accepted, offersSent);
  const shortlistRate = pct(shortlisted, resumes.length);

  // ── 5-stage workflow funnel ──────────────────────────────────────────────
  const totalAssessmentCandidates = preS.length + psycho.length + finals.length +
    pipelineStages.reduce((s, p) => s + (p.total || 0), 0);

  const workflowFunnel = [
    { key: 'jd',          label: 'Job Descriptions',  count: totalJDs,                      color: '#1e2d5e' },
    { key: 'media',       label: 'Media & Content',   count: jobSummary.withMediaPlan ?? 0, color: '#6366f1' },
    { key: 'assessment',  label: 'Assessment',        count: totalAssessmentCandidates,      color: '#f07060' },
    { key: 'resumes',     label: 'Resumes',           count: resumes.length,                 color: '#0891b2' },
    { key: 'offers',      label: 'Offer Letter',      count: offers.length,                  color: '#10b981' },
  ];


  // ── Assessment pass rates for bar chart — legacy + all custom pipeline stages
  const legacyAssessments = [
    { name: 'Pre-Screening',    total: preS.length,   passed: preS.filter(r => r.status==='shortlisted').length,   avgSc: avg(preS.filter(r=>r.score).map(r=>r.score)) },
    { name: 'Psychoanalytical', total: psycho.length, passed: psycho.filter(r => r.status==='shortlisted').length, avgSc: avg(psycho.filter(r=>r.score).map(r=>r.score)) },
    { name: 'Final Interview',  total: finals.length, passed: finals.filter(r => r.status==='shortlisted').length, avgSc: avg(finals.filter(r=>r.score).map(r=>r.score)) },
  ].filter(s => s.total > 0);

  // UUID pattern — entries whose label is still a raw UUID are noise, skip them
  const isUUID = str => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str?.trim() || '');

  // Include ALL configured pipeline stages (even those with 0 candidates — show as configured)
  const pipelineAssessments = pipelineStages
    .filter(s => s.label && !isUUID(s.label))   // drop UUID-labelled entries
    .map(s => ({
      name:    s.label,
      total:   s.total,
      passed:  s.passed,
      avgSc:   s.avgScore,
      invited: s.invited,
    }));

  // Deduplicate by label — if legacy and pipeline stage share the same name, merge them
  const assessmentMap = {};
  [...legacyAssessments, ...pipelineAssessments].forEach(a => {
    if (!a.name || isUUID(a.name)) return; // skip UUID-named entries
    const key = a.name.trim().toLowerCase();
    if (!assessmentMap[key]) {
      assessmentMap[key] = { ...a };
    } else {
      // Merge: sum totals, take max avgSc
      assessmentMap[key].total   = (assessmentMap[key].total  || 0) + (a.total  || 0);
      assessmentMap[key].passed  = (assessmentMap[key].passed || 0) + (a.passed || 0);
      if (a.avgSc != null) assessmentMap[key].avgSc = a.avgSc;
    }
  });
  // Only show stages that have actually been used (total > 0)
  const assessmentChart = Object.values(assessmentMap).filter(a => a.total > 0);

  // Pipeline table — use jobsDetail from backend (5 workflow stages per job)
  const pipelineRows = (data?.jobsDetail || []).sort((a, b) => b.resumes - a.resumes);


  return (
    <div className="p-5 space-y-4 bg-slate-50 min-h-screen">

      {/* ── Top bar ── */}
      {/* ── Top bar ── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1e2d5e]">
            {userName ? `${userName}'s` : 'My'} Recruitment Dashboard
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">
            {preset.days === -1 ? 'Custom range' : preset.label} · {company === 'all' ? 'All Companies' : company}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Date range presets */}
          <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <select
              value={preset.label}
              onChange={e => {
                const p = DATE_PRESETS.find(d => d.label === e.target.value);
                if (p) handlePresetChange(p);
              }}
              className="text-sm bg-transparent focus:outline-none"
            >
              {DATE_PRESETS.map(p => (
                <option key={p.label} value={p.label}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Custom date inputs */}
          {preset.days === -1 && (
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                className="text-sm bg-transparent focus:outline-none" />
              <span className="text-gray-400 text-sm">→</span>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                className="text-sm bg-transparent focus:outline-none" />
              <button onClick={() => load()}
                className="ml-1 px-2 py-0.5 bg-[#1e2d5e] text-white rounded text-xs font-semibold hover:bg-[#162347]">
                Apply
              </button>
            </div>
          )}

          {/* Company filter */}
          {companies.length > 0 && (
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <Building2 className="w-4 h-4 text-orange-400" />
              <select value={company} onChange={e => handleCompanyChange(e.target.value)}
                className="text-sm bg-transparent focus:outline-none">
                <option value="all">All Companies</option>
                {companies.map(c => <option key={c._id || c} value={c.name || c}>{c.name || c}</option>)}
              </select>
            </div>
          )}

          {/* Manual reload button */}
          <button
            onClick={() => load()}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#1e2d5e] text-white rounded-lg text-sm font-semibold hover:bg-[#162347] disabled:opacity-60 transition-colors shadow-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading…' : 'Reload'}
          </button>
        </div>
      </div>

      {/* ── Row 1: 3 metric panels + chart ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* JD Metrics */}
        <Card title="Job Descriptions">
          <MetricBlock value={fmt(totalJDs)}      label="Total JDs"         color={C.indigo} />
          <MetricBlock value={fmt(inProgressJDs)} label="Active / Hiring"   color={C.orange} />
          <MetricBlock value={fmt(jobSummary.completed ?? 0)} label="Completed"  color={C.emerald} />
          <MetricBlock value={fmt(jobSummary.withPipeline ?? 0)} label="With Pipeline" color={C.violet} />
          <button onClick={() => navigate('/jobs')}
            className="mt-3 text-xs font-bold text-[#1e2d5e] hover:underline flex items-center gap-1">
            <Briefcase className="w-3.5 h-3.5" /> View all JDs →
          </button>
        </Card>

        {/* Candidate Metrics */}
        <Card title="Candidate Pipeline">
          <MetricBlock value={fmt(resumes.length)} label="Total Applications" color={C.indigo} />
          <MetricBlock value={fmt(shortlisted)}     label="Shortlisted"         color={C.emerald} />
          <MetricBlock value={`${shortlistRate}`}   unit="%" label="Shortlist Rate"  color={C.orange} />
          <MetricBlock value={fmt(rejected)}         label="Rejected"            color={C.red} />
        </Card>

        {/* Offer Metrics */}
        <Card title="Offers & Acceptance">
          <MetricBlock value={fmt(offers.length)} label="Total Offers"        color={C.indigo} />
          <MetricBlock value={fmt(offersSent)}     label="Offers Sent"         color={C.violet} />
          <MetricBlock value={fmt(accepted)}       label="Accepted"            color={C.emerald} />
          <MetricBlock value={`${offerAccRate}`}   unit="%" label="Acceptance Rate" color={C.orange} />
        </Card>
      </div>

      {/* ── Row 2: Workflow Funnel (5 stages) + Assessment drill-down ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Workflow Funnel — 2 cols */}
        <Card title="Hiring Workflow" className="lg:col-span-2">
          <p className="text-xs text-gray-400 mb-4">Click a stage to explore details</p>
          <div className="space-y-3">
            {workflowFunnel.map((stage, i) => {
              const w = pct(stage.count, workflowFunnel[0].count);
              const isActive = activeStage === stage.key;
              return (
                <div key={stage.key}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <button
                      onClick={() => setActiveStage(isActive ? null : stage.key)}
                      className={`font-semibold transition-colors flex items-center gap-1.5 hover:underline ${isActive ? 'text-[#f07060]' : 'text-gray-700 hover:text-[#1e2d5e]'}`}
                    >
                      {stage.label}
                      {stage.key === 'assessment' && assessmentChart.length > 0 && (
                        <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-bold">
                          {assessmentChart.length} stages
                        </span>
                      )}
                    </button>
                    <span className="font-bold" style={{ color: stage.color }}>{fmt(stage.count)}</span>
                  </div>
                  <button
                    onClick={() => setActiveStage(isActive ? null : stage.key)}
                    className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden hover:opacity-80 transition-opacity"
                  >
                    <div
                      className="h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(4, w)}%`, backgroundColor: isActive ? '#f07060' : stage.color, opacity: isActive ? 1 : 0.75 }}
                    />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between text-xs text-gray-500">
            <span>JD → Offer conversion</span>
            <span className="font-bold text-[#1e2d5e]">
              {pct(accepted, totalJDs)}%
            </span>
          </div>
        </Card>

        {/* Assessment drill-down OR stage detail — 3 cols */}
        <Card
          title={activeStage === 'assessment' ? 'Assessment Usage' : stageDetail?.title ?? 'Stage Detail'}
          className="lg:col-span-3"
        >
          {!activeStage ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400 gap-2">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <ClipboardList className="w-6 h-6 opacity-40" />
              </div>
              <p className="text-sm font-medium">Click a funnel stage to explore</p>
              <p className="text-xs opacity-60">Assessment stage shows all types and usage rates</p>
            </div>
          ) : activeStage === 'assessment' ? (
            /* Assessment breakdown */
            <div className="space-y-3">
              {assessmentChart.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No assessments configured yet</p>
              ) : assessmentChart.map((a, i) => {
                const usageRate = pct(a.total, resumes.length);
                const passRate  = pct(a.passed, a.total);
                return (
                  <div key={i} className="p-3.5 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-800 text-sm">{a.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-medium">{a.total} used</span>
                        <span className="text-xs bg-[#f07060]/10 text-[#f07060] px-2 py-0.5 rounded-full font-medium">{usageRate}% usage</span>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-1.5">
                      <div className="h-2 rounded-full bg-[#1e2d5e]" style={{ width: `${usageRate}%` }} />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>{a.passed} passed · {a.total - a.passed - (a.invited ?? 0)} pending</span>
                      <span className={`font-semibold ${passRate >= 70 ? 'text-emerald-600' : passRate >= 50 ? 'text-gray-500' : 'text-red-400'}`}>
                        {passRate}% pass rate
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Other stage detail: pills + candidate list */
            <div className="space-y-3">
              {/* Stat pills */}
              <div className="grid grid-cols-3 gap-3">
                {stageDetail?.pills?.map((p, i) => (
                  <div key={i} className={`rounded-xl p-3 text-center ${p.cls}`}>
                    <div className="text-2xl font-extrabold">{fmt(p.value)}</div>
                    <div className="text-xs font-medium opacity-70 mt-0.5">{p.label}</div>
                  </div>
                ))}
              </div>

              {/* Candidate list */}
              {activeStage === 'jd' || activeStage === 'media' ? (
                <p className="text-xs text-gray-400 pt-1">
                  {activeStage === 'jd' ? 'Candidate data is tied to individual JDs.' : 'Content is generated per JD from the Media Plan & Content tab.'}
                  <button onClick={() => navigate('/jobs')} className="ml-1 text-[#1e2d5e] font-semibold hover:underline">View Jobs →</button>
                </p>
              ) : stageDetail?.items?.length > 0 ? (
                <>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-1">Candidates</p>
                  <div className="max-h-52 overflow-y-auto space-y-0.5">
                    {stageDetail.items.slice(0, 25).map((r, i) => (
                      <div key={i} className="flex items-center justify-between py-2.5 px-2 rounded-lg hover:bg-gray-50 border-b border-gray-50 last:border-0 transition-colors">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{r.candidate}</p>
                          <p className="text-xs text-gray-400 truncate">{r.jobTitle}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          {r.score > 0 && <span className="text-xs font-bold text-[#1e2d5e]">{Math.round(r.score)}%</span>}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            r.status === 'shortlisted' || r.status === 'accepted' ? 'bg-emerald-50 text-emerald-700' :
                            r.status === 'rejected'                               ? 'bg-red-50 text-red-600' :
                            'bg-gray-100 text-gray-500'}`}>
                            {r.status?.replace(/_/g,' ')}
                          </span>
                        </div>
                      </div>
                    ))}
                    {stageDetail.items.length > 25 && (
                      <p className="text-xs text-gray-400 text-center pt-1">+{stageDetail.items.length - 25} more</p>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No candidates yet in this stage</p>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* ── Bottom: Current Hiring Pipeline full width ── */}
      <Card title="Current Hiring Pipeline" className="w-full">
        {pipelineRows.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No pipeline data yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 pr-6 text-xs font-bold text-gray-400 uppercase tracking-wide">Role</th>
                  {['JD', 'Media & Content', 'Assessment', 'Resumes', 'Offer Letter'].map(h => (
                    <th key={h} className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wide whitespace-nowrap text-gray-400">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pipelineRows.map((row, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-indigo-50/30 transition-colors">
                    <td className="py-3.5 pr-6 font-semibold text-gray-800 max-w-[200px] truncate">{row.title}</td>
                    <td className="text-center py-3.5 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                        row.status === 'In-Progress' ? 'bg-[#f07060]/10 text-[#f07060]' :
                        row.status === 'Completed'   ? 'bg-emerald-50 text-emerald-700'  :
                        'bg-gray-100 text-gray-400'}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="text-center py-3.5 px-4">
                      <span className={`inline-flex w-8 h-8 rounded-lg items-center justify-center text-sm font-bold ${
                        row.hasMediaPlan ? 'bg-indigo-50 text-indigo-600' : 'text-gray-200'}`}>
                        {row.hasMediaPlan ? '✓' : '—'}
                      </span>
                    </td>
                    {[
                      { v: row.assessment, cls: 'bg-[#f07060]/10 text-[#f07060]' },
                      { v: row.resumes,    cls: 'bg-cyan-50 text-cyan-700'       },
                      { v: row.offers,     cls: 'bg-emerald-50 text-emerald-700' },
                    ].map(({ v, cls }, j) => (
                      <td key={j} className="text-center py-3.5 px-4">
                        {v > 0
                          ? <span className={`inline-flex w-8 h-8 rounded-lg items-center justify-center text-sm font-bold ${cls}`}>{v}</span>
                          : <span className="text-gray-300 font-semibold text-sm">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

    </div>
  );
}
